/** LEP-00005 — Enterprise Beta Platform framework (mirrors pkg/enterprisebeta) */

export const LEP_EB_ID = 'LEP-00005';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface EnrollmentRow {
  stage: string;
  count: number;
  status: string;
}

export interface MigrationRow {
  type: string;
  tenant: string;
  progress: number;
  status: string;
}

export interface ImplementationRow {
  step: string;
  tenant: string;
  status: string;
  progress: number;
}

export interface CSMSessionRow {
  capability: string;
  tenant: string;
  detail: string;
  status: string;
}

export interface SuccessMetricRow {
  metric: string;
  value: string;
  target: string;
  status: string;
}

export interface CommercialCheckRow {
  check: string;
  status: string;
  score: number;
}

export interface BetaOpsRow {
  category: string;
  name: string;
  value: string;
  status: string;
}

export const COMMAND_CENTER_METRICS = [
  'Customer Health', 'Business Health', 'Activation', 'Feature Adoption',
  'Marketplace Status', 'AI Adoption', 'Commercial Readiness', 'Reference Customer Status',
];

export const ENROLLMENT_STAGES = [
  'Applications', 'Qualification', 'Business Assessment', 'Marketplace Validation',
  'Approvals', 'Beta Agreements',
];

export const MIGRATION_TYPES = [
  'Product Migration', 'Catalog Migration', 'Order Migration', 'Inventory Migration',
  'Finance Migration', 'Advertising Migration', 'Validation', 'Rollback',
];

export const IMPLEMENTATION_STEPS = [
  'Tenant Setup', 'Marketplace Connection', 'DNA Initialization',
  'AI Configuration', 'Training', 'Production Validation',
];

export const CSM_CAPABILITIES = [
  'Implementation Guidance', 'Business Coaching', 'Marketplace Advice',
  'Optimization', 'Training', 'Adoption Planning',
];

export const SUCCESS_METRICS = [
  'Time to First Value', 'Revenue Growth', 'Business Outcomes',
  'AI Adoption', 'Automation Usage', 'ROI',
];

export const HEALTH_FACTORS = [
  'Usage', 'Training', 'Business Outcomes', 'Support Load',
  'Renewal Risk', 'Expansion Potential', 'Champion Score',
];

export const FEEDBACK_TYPES = [
  'Ideas', 'Pain Points', 'Workflow Issues', 'Performance Issues',
  'AI Feedback', 'Marketplace Feedback', 'Feature Requests',
];

export const COMMERCIAL_CHECKS = [
  'Plans', 'Billing', 'Credits', 'Invoices', 'Taxes', 'Renewals', 'Trials', 'Subscriptions',
];

export const SUPPORT_CAPABILITIES = [
  'Chat', 'Email', 'Knowledge Base', 'AI Assistance', 'Escalation', 'SLA Management',
];

export const COMMUNITY_FEATURES = [
  'Forums', 'Ideas', 'Templates', 'Prompt Sharing', 'Workflow Sharing', 'Events', 'Recognition',
];

export const BETA_ANALYTICS = [
  'Activation', 'Retention', 'Expansion', 'Business Growth',
  'Feature Usage', 'Support', 'Commercial Readiness',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Beta Command Center', 'Customer Enrollment', 'Migration Center', 'Implementation',
  'AI Customer Success Manager', 'Success Tracking', 'Commercial Readiness', 'Beta Operations',
];

export const LIVE_ROUTES: Record<string, string> = {
  customerSuccess: '/platform/customer-success',
  billing: '/billing',
  enterpriseAcceptance: '/platform/enterprise-acceptance',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Customer Health', value: '91/100', change: '+4', positive: true },
  { label: 'Business Health', value: '88/100', change: '+6', positive: true },
  { label: 'Activation', value: '94%', change: '+8%', positive: true },
  { label: 'Feature Adoption', value: '72%', change: '+15%', positive: true },
  { label: 'Marketplace Status', value: 'Connected', change: '12 tenants', positive: true },
  { label: 'AI Adoption', value: '86%', change: '+18%', positive: true },
  { label: 'Commercial Readiness', value: '92%', change: '+5%', positive: true },
  { label: 'Reference Customers', value: '4 active', change: '+2', positive: true },
];

export const DEMO_ENROLLMENT: EnrollmentRow[] = [
  { stage: 'Applications', count: 42, status: 'received' },
  { stage: 'Qualification', count: 28, status: 'qualified' },
  { stage: 'Business Assessment', count: 18, status: 'assessed' },
  { stage: 'Marketplace Validation', count: 14, status: 'validated' },
  { stage: 'Approvals', count: 12, status: 'approved' },
  { stage: 'Beta Agreements', count: 12, status: 'signed' },
];

export const DEMO_MIGRATIONS: MigrationRow[] = [
  { type: 'Catalog Migration', tenant: 'TechHub India', progress: 100, status: 'completed' },
  { type: 'Product Migration', tenant: 'TechHub India', progress: 100, status: 'completed' },
  { type: 'Inventory Migration', tenant: 'StyleCraft', progress: 85, status: 'in_progress' },
  { type: 'Order Migration', tenant: 'StyleCraft', progress: 60, status: 'in_progress' },
  { type: 'Finance Migration', tenant: 'GreenLeaf Organics', progress: 40, status: 'pending' },
  { type: 'Advertising Migration', tenant: 'GreenLeaf Organics', progress: 0, status: 'scheduled' },
];

export const DEMO_IMPLEMENTATION: ImplementationRow[] = [
  { step: 'Tenant Setup', tenant: 'TechHub India', status: 'completed', progress: 100 },
  { step: 'Marketplace Connection', tenant: 'TechHub India', status: 'completed', progress: 100 },
  { step: 'DNA Initialization', tenant: 'TechHub India', status: 'completed', progress: 100 },
  { step: 'AI Configuration', tenant: 'StyleCraft', status: 'in_progress', progress: 75 },
  { step: 'Training', tenant: 'StyleCraft', status: 'in_progress', progress: 50 },
  { step: 'Production Validation', tenant: 'GreenLeaf Organics', status: 'pending', progress: 0 },
];

export const DEMO_CSM: CSMSessionRow[] = [
  { capability: 'Implementation Guidance', tenant: 'TechHub India', detail: 'Complete Prime Day campaign setup', status: 'active' },
  { capability: 'Business Coaching', tenant: 'StyleCraft', detail: 'Reach ₹20L/mo revenue target', status: 'active' },
  { capability: 'Marketplace Advice', tenant: 'GreenLeaf Organics', detail: 'Enable FBA for top 50 SKUs', status: 'scheduled' },
  { capability: 'Adoption Planning', tenant: 'TechHub India', detail: 'Q3 adoption plan — 90% feature coverage', status: 'completed' },
];

export const DEMO_SUCCESS: SuccessMetricRow[] = [
  { metric: 'Time to First Value', value: '3.2 days', target: '<7 days', status: 'exceeded' },
  { metric: 'Revenue Growth', value: '+24%', target: '+15%', status: 'exceeded' },
  { metric: 'AI Adoption', value: '86%', target: '>70%', status: 'exceeded' },
  { metric: 'Automation Usage', value: '58%', target: '>50%', status: 'exceeded' },
  { metric: 'ROI', value: '5.2x', target: '>3x', status: 'exceeded' },
];

export const DEMO_COMMERCIAL: CommercialCheckRow[] = [
  { check: 'Plans', status: 'validated', score: 100 },
  { check: 'Billing', status: 'validated', score: 98 },
  { check: 'Credits', status: 'validated', score: 96 },
  { check: 'Invoices', status: 'validated', score: 99 },
  { check: 'Taxes', status: 'validated', score: 94 },
  { check: 'Renewals', status: 'validated', score: 97 },
  { check: 'Trials', status: 'validated', score: 100 },
  { check: 'Subscriptions', status: 'validated', score: 98 },
];

export const DEMO_BETA_OPS: BetaOpsRow[] = [
  { category: 'Support', name: 'Avg Response Time', value: '2.4h', status: 'within_sla' },
  { category: 'Support', name: 'Open Tickets', value: '8', status: 'healthy' },
  { category: 'Community', name: 'Active Members', value: '42', status: 'growing' },
  { category: 'Feedback', name: 'Feature Requests', value: '28', status: 'triaged' },
  { category: 'Feedback', name: 'Pain Points', value: '6', status: 'addressed' },
  { category: 'Health', name: 'Champion Score', value: '8.4/10', status: 'strong' },
];

export function buildBetaDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    enrollment_stages: ENROLLMENT_STAGES.length,
    migration_types: MIGRATION_TYPES.length,
    success_metrics: SUCCESS_METRICS.length,
    commercial_checks: COMMERCIAL_CHECKS.length,
  };
}
